import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import startOfDay from "date-fns/startOfDay";
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import StaticDatePicker from "@mui/lab/StaticDatePicker";
import PickersDay from "@mui/lab/PickersDay";
import "./MultiDatePicker.css";
import { format } from "date-fns";
import { useState, useEffect } from "react";

const startDateWithMonthWith31 = new Date('05-01-2024');
const endDateWithMonthWith31 = new Date('05-31-2024');

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== "selected"
})(({ theme, selected }) => ({
  ...(selected && {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    "&:hover, &:focus": {
      backgroundColor: theme.palette.primary.dark
    },
    borderTopLeftRadius: "50%",
    borderBottomLeftRadius: "50%",
    borderTopRightRadius: "50%",
    borderBottomRightRadius: "50%"
  })
}));

interface Props {
  value: number[];
  onChange?: (days: number[]) => void;
}

export function MultiDatePicker({ value, onChange }: Props) {
  const [internalValues, setInternalValues] = useState(() => {
    if (!value) return [startOfDay(startDateWithMonthWith31)]

    return value.map((day) => {
      const date = new Date('05-' + day + '-2024');
      return startOfDay(date);
    });
  });

  useEffect(() => {
    if (value) {
      setInternalValues(value.map((day) => {
        const date = new Date('05-' + day + '-2024');
        return startOfDay(date);
      }));
    }
  }, [value]);

  const findDate = (dates: Date[], date: Date) => {
    const dateTime = date.getTime();
    return dates.find((item) => item.getTime() === dateTime);
  };

  const findIndexDate = (dates: Date[], date: Date) => {
    const dateTime = date.getTime();
    return dates.findIndex((item) => item.getTime() === dateTime);
  };

  const renderPickerDay = (date: Date, _selectedDates: Date[], pickersDayProps: any) => {
    if (!internalValues) {
      return <PickersDay {...pickersDayProps} />;
    }

    const selected = findDate(internalValues, date);

    return (
      <CustomPickersDay
        {...pickersDayProps}
        disableMargin
        selected={selected}
      />
    );
  };

  const handleDateChange = (newValue: Date) => {
    const array = [...internalValues];
    const date = startOfDay(newValue);
    const index = findIndexDate(array, date);
    if (index >= 0) {
      array.splice(index, 1);
    } else {
      array.push(date);
    }

    setInternalValues(array);
    onChange?.(array.sort((a, b) => a.getTime() - b.getTime()).map((date) => Number(format(date, "d"))));
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className="calendar-remove-header">
        <StaticDatePicker
          displayStaticWrapperAs="desktop"
          label="Week picker"
          value={internalValues}
          defaultCalendarMonth={startDateWithMonthWith31}
          minDate={startDateWithMonthWith31}
          maxDate={endDateWithMonthWith31}
          componentsProps={{
            leftArrowButton: { sx: { display: 'none' } },
            rightArrowButton: { sx: { display: 'none' } },
          }}
          onChange={handleDateChange}
          renderDay={renderPickerDay}
          views={["day"]}
          outsideCurrentMonth
          renderInput={(params) => <TextField {...params} />}
          inputFormat="'Week of' MMM d"
        />
      </div>
    </LocalizationProvider>
  );
}
